import React from 'react';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  TextField,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FilterButton, StyledActionButton } from './SectionBar.styled';

interface CN19ReportFiltersProps {
  customFilters: {
    ppo?: string;
    year?: string;
    currency?: string;
    ppoOptions?: Array<{ id: string; name: string; disabled: boolean }>;
    yearOptions?: string[];
    isPpoLoading?: boolean;
    onPpoChange?: (value: string) => void;
    onYearChange?: (value: string) => void;
    onCurrencyChange?: (value: string) => void;
    onEnterIbrsRates?: () => void;
    isProcessing?: boolean;
  };
}

const CN19ReportFilters: React.FC<CN19ReportFiltersProps> = ({ customFilters }) => {
  // State for dropdown menus
  const [ppoAnchorEl, setPpoAnchorEl] = React.useState<null | HTMLElement>(null);
  const [yearAnchorEl, setYearAnchorEl] = React.useState<null | HTMLElement>(null);

  const isPpoMenuOpen = Boolean(ppoAnchorEl);
  const isYearMenuOpen = Boolean(yearAnchorEl);

  // Generate year options (from current year down to 2008)
  const defaultYearOptions = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years: string[] = [];
    for (let year = currentYear; year >= 2008; year--) {
      years.push(year.toString());
    }
    return years;
  }, []);

  const yearOptions = customFilters.yearOptions || defaultYearOptions;

  // Handlers
  const handlePpoClick = (event: React.MouseEvent<HTMLElement>) => {
    setPpoAnchorEl(event.currentTarget);
  };

  const handlePpoMenuClose = () => {
    setPpoAnchorEl(null);
  };

  const handlePpoChange = (ppo: string) => {
    customFilters.onPpoChange?.(ppo);
    handlePpoMenuClose();
  };

  const handleYearClick = (event: React.MouseEvent<HTMLElement>) => {
    setYearAnchorEl(event.currentTarget);
  };

  const handleYearMenuClose = () => {
    setYearAnchorEl(null);
  };

  const handleYearChange = (year: string) => {
    customFilters.onYearChange?.(year);
    handleYearMenuClose();
  };

  const handleCurrencyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    customFilters.onCurrencyChange?.(event.target.value);
  };

  // Display labels
  const getPpoLabel = () => {
    if (!customFilters.ppo) return 'ALL';
    return customFilters.ppo;
  };

  const getYearLabel = () => {
    if (!customFilters.year) return 'Select Year';
    return customFilters.year;
  };

  return (
    <>
      {/* PPO Filter */}
      <FilterButton onClick={handlePpoClick}>
        <span className='label'>DO</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getPpoLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={ppoAnchorEl}
        open={isPpoMenuOpen}
        onClose={handlePpoMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '200px',
          },
        }}
      >
        <MenuItem onClick={() => handlePpoChange('')}>
          <Typography sx={{ fontWeight: 600 }}>ALL</Typography>
        </MenuItem>
        {customFilters.ppoOptions
          ?.filter(operator => !operator.disabled)
          ?.map((operator) => (
            <MenuItem
              key={operator.id}
              onClick={() => handlePpoChange(operator.name)}
              selected={customFilters.ppo === operator.name}
              sx={{
                fontWeight: customFilters.ppo === operator.name ? 700 : 400,
                fontSize: '14px',
              }}
            >
              {operator.name}
            </MenuItem>
          ))}
      </Menu>

      {/* Year Filter */}
      <FilterButton onClick={handleYearClick}>
        <span className='label'>Year</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getYearLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={yearAnchorEl}
        open={isYearMenuOpen}
        onClose={handleYearMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '150px',
          },
        }}
      >
        <MenuItem onClick={() => handleYearChange('')}>
          <Typography sx={{ fontStyle: 'italic', color: '#666' }}>Clear</Typography>
        </MenuItem>
        {yearOptions.map((year) => (
          <MenuItem
            key={year}
            onClick={() => handleYearChange(year)}
            selected={customFilters.year === year}
            sx={{
              fontWeight: customFilters.year === year ? 700 : 400,
              fontSize: '14px',
            }}
          >
            {year}
          </MenuItem>
        ))}
      </Menu>

      {/* Currency Input */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#063142', minWidth: 'max-content' }}>
          Currency:
        </Typography>
        <TextField
          size="small"
          value={customFilters.currency || ''}
          onChange={handleCurrencyChange}
          placeholder="Enter currency"
          sx={{
            minWidth: '120px',
            '& .MuiOutlinedInput-root': {
              height: '32px',
              fontSize: '14px',
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: '#D1D9E0',
              },
              '&:hover fieldset': {
                borderColor: '#00ACE7',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#00ACE7',
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: '8px 14px',
            },
          }}
        />
      </Box>

      {/* Enter IBRS Rates Button */}
      <StyledActionButton
        onClick={() => customFilters.onEnterIbrsRates?.()}
        disabled={customFilters.isProcessing}
        sx={{
          backgroundColor: '#00ACE7',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#0095C7',
          },
          '&:disabled': {
            backgroundColor: '#B8C4CE',
            color: '#6C757D',
          }
        }}
      >
        <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
          Enter IBRS Rates
        </Typography>
      </StyledActionButton>
    </>
  );
};

export default CN19ReportFilters; 